import PropTypes from 'prop-types'
import {
  Lock,
  X,
  ArrowLeft,
  HandArrowDown,
  Steps,
  UserCircle,
  Bank,
  MoneyWavy,
  WarningCircle,
  Clock,
  Info,
  CaretDown,
  Scan,
  Question,
  ShieldCheck,
  CircleNotch,
  CheckCircle,
  Check,
  ChatText,
  EnvelopeSimple,
  CurrencyCircleDollar,
  UserFocus,
  User,
  Copy,
  CheckSquare,
  MagnifyingGlass,
  StarFour,
} from '@phosphor-icons/react'

// Semantic name -> fixed {Icon, weight, size, color} per spec §4's icon table. `color` may be
// a design-token var() or a one-off literal hex for icons the token system doesn't cover
// (e.g. the "how it works" step icons, which use colours not otherwise used in the UI).
const ICON_MAP = {
  'header-lock': { Icon: Lock, weight: 'regular', size: 12, color: 'var(--dd-color-success)' },
  close: { Icon: X, weight: 'regular', size: 16, color: 'var(--dd-color-text-tertiary)' },
  back: { Icon: ArrowLeft, weight: 'regular', size: 16, color: 'var(--dd-color-text-headings)' },
  'hero-hand': { Icon: HandArrowDown, weight: 'regular', size: 50, color: 'var(--dd-color-info)' },
  'how-it-works-chip': { Icon: Steps, weight: 'regular', size: 14, color: 'var(--dd-color-info)' },
  'step-verify': { Icon: UserCircle, weight: 'regular', size: 16, color: 'var(--dd-color-success)' },
  'step-bank': { Icon: Bank, weight: 'fill', size: 16, color: '#6B3FE0' },
  'step-send': { Icon: MoneyWavy, weight: 'regular', size: 16, color: '#C944D3' },
  'helper-warning': { Icon: WarningCircle, weight: 'regular', size: 16, color: 'var(--dd-color-text-tertiary)' },
  'helper-error': { Icon: WarningCircle, weight: 'regular', size: 16, color: 'var(--dd-color-error)' },
  'callout-warning-note': { Icon: Info, weight: 'regular', size: 12, color: 'var(--dd-color-brand)' },
  'terminal-clock': { Icon: Clock, weight: 'regular', size: 28, color: 'var(--dd-color-text-tertiary)' },
  'terminal-warning': { Icon: WarningCircle, weight: 'regular', size: 28, color: 'var(--dd-color-warn-text)' },
  'select-caret': { Icon: CaretDown, weight: 'regular', size: 16, color: 'var(--dd-color-text-secondary)' },
  'select-caret-dial': { Icon: CaretDown, weight: 'regular', size: 12, color: 'var(--dd-color-text-secondary)' },
  'select-check': { Icon: Check, weight: 'bold', size: 16, color: 'var(--dd-color-brand)' },
  'sheet-scan': { Icon: Scan, weight: 'regular', size: 20, color: 'var(--dd-color-brand)' },
  'sheet-question': { Icon: Question, weight: 'regular', size: 20, color: 'var(--dd-color-brand)' },
  'inline-code-shield': { Icon: ShieldCheck, weight: 'regular', size: 16, color: 'var(--dd-color-text-primary)' },
  spinner: { Icon: CircleNotch, weight: 'regular', size: 16, color: 'var(--dd-color-brand)' },
  'verified-fill': { Icon: CheckCircle, weight: 'fill', size: 16, color: 'var(--dd-color-success)' },
  'verified-row-check': { Icon: CheckCircle, weight: 'regular', size: 20, color: 'var(--dd-color-surface)' },
  'switch-channel-sms': { Icon: ChatText, weight: 'regular', size: 16, color: 'var(--dd-color-text-secondary)' },
  'switch-channel-email': { Icon: EnvelopeSimple, weight: 'regular', size: 16, color: 'var(--dd-color-text-secondary)' },
  'table-currency': { Icon: CurrencyCircleDollar, weight: 'regular', size: 16, color: 'var(--dd-color-text-secondary)' },
  'table-user-focus': { Icon: UserFocus, weight: 'regular', size: 16, color: 'var(--dd-color-text-secondary)' },
  'table-user': { Icon: User, weight: 'regular', size: 16, color: 'var(--dd-color-text-secondary)' },
  'table-bank': { Icon: Bank, weight: 'regular', size: 16, color: 'var(--dd-color-text-secondary)' },
  'table-copy': { Icon: Copy, weight: 'regular', size: 14, color: 'var(--dd-color-text-secondary)' },
  'saved-account-selected': { Icon: CheckSquare, weight: 'fill', size: 20, color: 'var(--dd-color-brand)' },
  search: { Icon: MagnifyingGlass, weight: 'regular', size: 12, color: 'var(--dd-color-text-tertiary)' },
  'confetti-star-brand': { Icon: StarFour, weight: 'fill', size: 9, color: 'var(--dd-color-brand)' },
  'confetti-star-red': { Icon: StarFour, weight: 'fill', size: 8, color: '#E94E3C' },
}

/**
 * Thin semantic wrapper over @phosphor-icons/react, keyed to spec §4's fixed
 * per-location icon table so screens don't repeat weight/size/color at each call site.
 * Decorative by default (aria-hidden) per spec §4/§10 — icons are never the accessible
 * label themselves; a button whose only content is an icon must carry its own aria-label.
 */
const DDIcon = ({ name, size, color, weight, className, style }) => {
  const def = ICON_MAP[name]
  if (!def) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`DDIcon: unknown icon name "${name}"`)
    }
    return null
  }
  const { Icon } = def
  return (
    <Icon
      aria-hidden='true'
      weight={weight ?? def.weight}
      size={size ?? def.size}
      color={color ?? def.color}
      className={className}
      style={style}
    />
  )
}

DDIcon.propTypes = {
  name: PropTypes.oneOf(Object.keys(ICON_MAP)).isRequired,
  size: PropTypes.number,
  color: PropTypes.string,
  weight: PropTypes.oneOf(['thin', 'light', 'regular', 'bold', 'fill', 'duotone']),
  className: PropTypes.string,
  style: PropTypes.object,
}

export default DDIcon
